import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import {
  CELL_HEIGHT,
  CELL_WIDTH,
  checkExportItemHasAllCalibrationsLoaded,
  EXPORT_CONTENT_TYPES,
  EXPORT_FIXED_TABS,
  EXPORT_TOASTS,
  EXPORT_TYPES,
  getEmptyExportData,
  getExportChartOptions,
  getStartColumn,
  IExportChartsOptions,
  IExportFilterType,
  IExportFixedTab,
  prepareSheetNameForExport
} from '@components/export/export.config';
import { betaChartJSON } from '@components/export/highcharts/beta-chart-export';
import { createScatterPlotJSON } from '@components/export/highcharts/beta-scatter-plot-export';
import { decayChartJSON } from '@components/export/highcharts/chart-decay-export';
import { productSummaryChartJSON, productSummaryChartOptions } from '@components/export/highcharts/product-summary-chart-export';
import { stableBalanceChartJSON } from '@components/export/highcharts/stable-balance-export';
import { addBetaChangeLogToSheet } from '@components/export/spread/beta/beta-change-log';
import { addBetaEstimatesToSheet } from '@components/export/spread/beta/estimate-ouput';
import { createCalibrationSettingsExport } from '@components/export/spread/calibration-settings/calibration-settings-export';
import { createConcentrationReportExport } from '@components/export/spread/concentration-report/concentration-report-export';
import { addDecayChangeLogToSheet } from '@components/export/spread/decay/decay-change-logs';
import { addDecayEstimateDetailToSheet } from '@components/export/spread/decay/decay-estimate-detail';
import { addDecayFactorsToSheet } from '@components/export/spread/decay/decay-factors-export';
import { createProductDetailActualExport } from '@components/export/spread/product-detail/product-detail-actual-export';
import { createRunOffScheduleExport } from '@components/export/spread/product-detail/product-detail-run-off-schedule-export';
import { buildProductSummaryData, createProductSummaryExport } from '@components/export/spread/product-summary/product-summary-export';
import { createHaircutInfo } from '@components/export/spread/stable-balance/haircut-info-export';
import { addStableBalanceModelFitToSheet } from '@components/export/spread/stable-balance/model-fit';
import { addStableBalanceChangeLogToSheet } from '@components/export/spread/stable-balance/stable-balance-change-log-export';
import * as GC from '@grapecity/spread-sheets';
import { IAcquisition } from '@models/acquisition';
import { IBetaCalibration, IBetaEstimateResult } from '@models/beta/beta-estimate';
import { IMarketRate } from '@models/beta/marketrate';
import { IZoomLevel } from '@models/beta/zoomlevel';
import { CALIBRATION_TYPES } from '@models/calibrations/calibration';
import { ICalibrationChangelog } from '@models/calibrations/calibration-changelog';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { DecayCalibrationType } from '@models/decay/decay-calibration-type';
import { IDecayQuery } from '@models/decay/decay-estimate';
import { IExportData, IExportItem } from '@models/export/export-item';
import { IExportChartRequestItem, IExportChartResponseItem } from '@models/export/export-request';
import { IProductConcentrationDetails, IProductGroup } from '@models/product';
import { IVintageRunOff, IVintageRunOffSchedule, VintageRunOffType } from '@models/product-detail-report/vintage-run-off';
import { CalibrationFilter, CalibrationStatus, isActive } from '@models/record-status';
import { IProductCPSummaryItem, IProductSummaryResponse } from '@models/reports/current-month-summary';
import { IStableBalanceCalibration } from '@models/stable-balance/stable-balance-calibration';
import { IStoreState } from '@models/store/store-state';
import { ChangelogService } from '@services/changelog/changelog.service';
import { DecayCalibrationTypeService } from '@services/decay/decay-calibration-type.service';
import { DecayClosedAccountsService } from '@services/decay/decay-closed-accounts.service';
import { AcquisitionService } from '@services/global/acquisition.service';
import { FunctionalFormService } from '@services/global/functional-form.service';
import { ObservationDatesService } from '@services/global/observation-dates.service';
import { ProductService } from '@services/global/product.service';
import { ToastService } from '@services/global/toast.service';
import { ObservableStore } from '@services/observable-store/dist';
import { ProductDetailService } from '@services/product-detail/product-detail.service';
import { MarketRateService } from '@services/settings/market-rate.service';
import { clone, flat } from '@shared/arrays';
import { getDateString, getLastDayOfNextMonth } from '@shared/dates';
import { processDecayBalanceResult } from '@shared/decay-helpers';
import { round } from '@shared/numbers';
import { exportWorkbookToExcel } from '@shared/spread/export-workbook-to-excel';
import { addSheet, removeSheetByName, setRowsAndColumns } from '@shared/spread/sheet-functions';
import { FONT_NAME } from '@shared/spread/spread-settings';
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, skip, timeout } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ExportService extends ObservableStore<IStoreState> {
  // Setting the max number of rows so that data is not lost if there are large volumes of data (BUG 54338)
  // E.g. 10 Beta estimates will go past the default number of row of 200
  // Larger time series will also start to hit the 200 rows issue
  private readonly MAX_ROW_COUNT = 1_500;
  // 1,000 is chosen as the larger the number the large the memory needed to create the sheet
  private readonly MAX_COL_COUNT = 1_000;
  private HIGHCHARTS_EXPORT_URL: string;
  private exportAPIEndpoint: string;
  public lastObservationDate: Date = null;
  public acquisitions: IAcquisition[] = null;
  public activeProductGroups: IProductGroup[] = null;

  public exportFilterType: IExportFilterType = EXPORT_TYPES.propose.id;
  public fixedTabs: IExportFixedTab[] = clone(EXPORT_FIXED_TABS);

  // We use this array to export just the items that are checked-on and apply the export filter to the calibrations too
  public filteredExportItems: IExportItem[] = null;

  public exportWorkbook: GC.Spread.Sheets.Workbook = new GC.Spread.Sheets.Workbook();
  public exportWorkbook$ = new BehaviorSubject(null);
  public buildingExportPreview = false;
  public retrievingDataIds = [];
  public exportData: IExportData = getEmptyExportData();
  public readonly exportDataRequest$ = new Subject<boolean>();
  public readonly exportDataRequestProductIds = new Set<number>();
  public readonly exportDataRequestFixedTabIds = new Set<string>();

  constructor(
    private readonly acquisitionService: AcquisitionService,
    private readonly appConfig: AppConfigProvider,
    private readonly changelogService: ChangelogService,
    private readonly decayCalibrationTypeService: DecayCalibrationTypeService,
    private readonly decayClosedAccounts: DecayClosedAccountsService,
    private readonly functionalFormService: FunctionalFormService,
    private readonly http: HttpClient,
    private readonly marketRateService: MarketRateService,
    private readonly observationDatesService: ObservationDatesService,
    private readonly productDetailService: ProductDetailService,
    private readonly productService: ProductService,
    private readonly toastService: ToastService
  ) {
    super({ trackStateHistory: false });

    this.appConfig.environment.subscribe(env => {
      this.exportAPIEndpoint = `${env.API.REFERENCE_DATA}/api/export`;
      this.HIGHCHARTS_EXPORT_URL = `${env.API.EXPORTER}/api/generate-charts`;
    });

    this.initSubscriptions();
  }

  public resetValues() {
    // Reset values to Default once the export-preview is closed
    this.exportFilterType = EXPORT_TYPES.propose.id;
    this.exportData = getEmptyExportData();
    this.filteredExportItems = null;
    this.fixedTabs = clone(EXPORT_FIXED_TABS);
    this.retrievingDataIds = [];
  }

  private initSubscriptions() {
    // ==== Sources ====
    const exportWorkbook$ = this.exportWorkbook$.pipe(
      // Ignore null or falsy values
      filter(workbook => workbook)
    );
    // Export Data Request
    const exportDataRequest = this.exportDataRequest$.pipe(
      // group multiple Ids in the same call
      skip(1),
      debounceTime(150)
    );
    // const prebuildWorkbook = this.prebuildWorkbook$.pipe(debounceTime(200));

    // ==== Subscriptions ====
    exportWorkbook$.subscribe(exportWorkbookToExcel);
    exportDataRequest.subscribe(() => this.onExportDataRequest());
  }

  private onExportDataRequest() {
    if (this.exportData && this.exportData.ExportItems) {
      // Request data just for those Ids that we don't have data loaded yet
      const productIds = [...this.exportDataRequestProductIds].filter(id => {
        const elem = this.exportData.ExportItems.find(({ Product }) => Product.ProductId === id);
        return elem && !elem.IsFullyLoaded;
      });
      const fixedTabIds = [...this.exportDataRequestFixedTabIds].filter(id => {
        const elem = this.fixedTabs.find(e => e.id === id);
        return elem && !elem.IsFullyLoaded;
      });
      if ([...productIds, ...fixedTabIds].length > 0) {
        this.loadExportComplementaryData(productIds, fixedTabIds);
      }
    }
  }

  public filterExportProductCalibrations() {
    const applyExportFilter = prodCalibs => {
      return prodCalibs.filter(cal => {
        if (this.exportFilterType === EXPORT_TYPES.active.id) {
          // Ignore propose calibrations
          return cal.RecordStatus !== CalibrationStatus.Proposed;
        }
        return true;
      });
    };

    // Filter Propose records or just Active ones based on the selected switch option
    if (this.exportData && this.exportData.ExportItems && this.exportData.ExportItems.length > 0) {
      this.filteredExportItems = this.exportData.ExportItems;

      this.exportData.ExportItems.forEach((exportItem, index) => {
        // <15426> Fill ProductGroupName, needed for proper sorting
        const group = this.activeProductGroups.find(g => g.ProductGroupId === exportItem.Product.ProductGroupId);
        if (group) {
          exportItem.Product.ProductGroupName = group.GroupName;
        }

        if (exportItem.Beta) {
          this.filteredExportItems[index].Beta.ProductCalibrations = applyExportFilter(exportItem.Beta.ProductCalibrations);
        }
        if (exportItem.Decay) {
          this.filteredExportItems[index].Decay.ProductCalibrations = applyExportFilter(exportItem.Decay.ProductCalibrations);
        }
        if (exportItem.StableBalance) {
          this.filteredExportItems[index].StableBalance.ProductCalibrations = applyExportFilter(
            exportItem.StableBalance.ProductCalibrations
          );
        }
      });
    }
  }

  public async getHighChartsImage(
    charts: IExportChartRequestItem[] | IExportChartsOptions[],
    width?: number
  ): Promise<IExportChartResponseItem[]> {
    const postData = { charts };
    return await this.http
      .post<IExportChartResponseItem[]>(this.HIGHCHARTS_EXPORT_URL, postData)
      .pipe(
        timeout(60_000 * 10), // 10 minutes
        map(res => {
          res.forEach(item => (item.image = `data:image/png;base64,${item.image}`));
          return res;
        }),
        catchError(err => {
          console.error(err);
          this.toastService.showToast(EXPORT_TOASTS.getHighchartsImageError);
          return [];
        })
      )
      .toPromise();
  }

  public async loadExportEssentialData(productIds: number[]): Promise<IExportData> {
    // Load shared data from the state
    const [acquisitions, lastObsDate, prodGroups] = await forkJoin([
      this.acquisitionService.getAcquisitions(),
      this.observationDatesService.getLastObservationDate(),
      this.productService.getProductGroups().pipe(map(groups => groups.filter(({ IsActive }) => IsActive)))
    ]).toPromise();
    this.acquisitions = acquisitions;
    this.lastObservationDate = lastObsDate;
    this.activeProductGroups = prodGroups;

    return this.buildExportData(productIds, [], false);
  }

  public async loadExportComplementaryData(productIds: number[], fixedTabIds: string[]): Promise<IExportData> {
    const result = await this.buildExportData(productIds, fixedTabIds, true);

    // Clear the identifiers
    this.exportDataRequestFixedTabIds.clear();
    this.exportDataRequestProductIds.clear();

    return result;
  }

  private async buildExportData(productIds: number[], fixedTabIds: string[], complementaryData: boolean = false): Promise<IExportData> {
    // Request just the ids of the items missing in the preloaded data

    fixedTabIds = fixedTabIds.filter(id => !this.retrievingDataIds.includes(id) && !this.fixedTabs.find(tab => tab.id).IsFullyLoaded);
    productIds = productIds.filter(id => !this.retrievingDataIds.includes(id));

    if (fixedTabIds.length > 0) {
      // We need all the products and his product calibrations If we want to build the fixed tabs
      productIds = this.exportData.ExportItems.filter(
        item => !item.IsFullyLoaded && !this.retrievingDataIds.includes(item.Product.ProductId)
      ).map(({ Product }) => Product.ProductId);
    } else if (this.exportData && this.exportData.ExportItems) {
      // Avoid subsequent calls:
      // -- Products: Filter to use just those productIds that has not been fully loaded yet
      productIds = this.exportData.ExportItems.filter(item => !item.IsFullyLoaded && productIds.includes(item.Product.ProductId)).map(
        ({ Product }) => Product.ProductId
      );
      // Fixed tabs: Filter to use just those tab ids that has not been fully loaded yet
      fixedTabIds = complementaryData // complementaryData is mandatory to get the fixedTabs data
        ? fixedTabIds.filter(id => {
            const tab = this.fixedTabs.find(t => t.id === id);
            return tab && !tab.IsFullyLoaded && fixedTabIds.includes(tab.id);
          })
        : [];
    }

    if ([...productIds, ...fixedTabIds].length > 0) {
      // Add Ids and make the array unique
      this.retrievingDataIds = [...new Set([...this.retrievingDataIds, ...productIds, ...fixedTabIds])];

      const params = {
        ProductIds: productIds,
        FixedTabIds: fixedTabIds,
        ComplementaryData: complementaryData,
        LastObservationDate: getDateString(this.lastObservationDate)
      };

      const dataRequested = await this.http
        .post(this.exportAPIEndpoint, params)
        .pipe(
          map(response => this.toPascal(response) as IExportData),
          map(response => this.processExportData(response)),
          catchError(err => {
            console.error(err);
            this.toastService.showToast(EXPORT_TOASTS.getExportDataError);
            return Promise.resolve(getEmptyExportData());
          })
        )
        .toPromise();

      this.addToExportData(dataRequested, complementaryData);

      // Remove the Ids from the array
      [...productIds, ...fixedTabIds].forEach(id => this.retrievingDataIds.splice(this.retrievingDataIds.indexOf(id), 1));
    }
    return this.exportData;
  }

  private toPascal(o) {
    let newO;
    let origKey;
    let newKey;
    let value;

    if (o instanceof Array) {
      return o.map(val => {
        if (typeof val === 'object') {
          val = this.toPascal(val);
        }
        return val;
      });
    } else {
      newO = {};
      for (origKey in o) {
        if (o.hasOwnProperty(origKey)) {
          newKey = (origKey.charAt(0).toUpperCase() + origKey.slice(1) || origKey).toString();
          value = o[origKey];
          if (value instanceof Array || (value !== null && value.constructor === Object)) {
            value = this.toPascal(value);
          }
          newO[newKey] = value;
        }
      }
    }
    return newO;
  }

  private addToExportData(dataRetrieved: IExportData, complementaryData: boolean): IExportData {
    if (!this.exportData || !this.exportData.ExportItems) {
      this.exportData = dataRetrieved;
    } else if (complementaryData) {
      // ExportItems
      if (this.exportData.ExportItems && dataRetrieved.ExportItems) {
        dataRetrieved.ExportItems.forEach((expItem, reqInd) => {
          const expInd = this.exportData.ExportItems.findIndex(({ Product }) => Product.ProductId === expItem.Product.ProductId);
          const newItemData = dataRetrieved.ExportItems[reqInd];
          this.updateExportItem(expInd, newItemData);
        });
      }

      // CurrentPeriodSummary
      if (this.exportData.CurrentPeriodSummary && dataRetrieved.CurrentPeriodSummary) {
        this.exportData.CurrentPeriodSummary = dataRetrieved.CurrentPeriodSummary;
      }

      // ConcentrationReport
      if (this.exportData.ConcentrationReport && dataRetrieved.ConcentrationReport) {
        this.exportData.ConcentrationReport = dataRetrieved.ConcentrationReport;
      }

      const everyProductHasCalibrations = this.exportData.ExportItems.every(checkExportItemHasAllCalibrationsLoaded);
      if (everyProductHasCalibrations) {
        this.fixedTabs.find(tab => tab.id === 'calibration-settings').IsFullyLoaded = true;
        if (this.exportData.ConcentrationReport) {
          this.fixedTabs.find(({ id }) => id === 'concentration-report').IsFullyLoaded = true;
        }
        if (this.exportData.CurrentPeriodSummary) {
          this.fixedTabs.find(({ id }) => id === 'product-summary').IsFullyLoaded = true;
        }
      }
    }

    return this.exportData;
  }

  private updateExportItem(expInd: number, newItemData: IExportItem) {
    // Which properties do we want to overwrite ?

    // Process Decay Balance Result
    if (newItemData.Decay && newItemData.Decay.DecayResult && newItemData.Decay.DecayResult.VintageObservationValues) {
      newItemData.Decay.DecayResult = processDecayBalanceResult(newItemData.Decay.DecayResult);
    }
    this.exportData.ExportItems[expInd].Decay = newItemData.Decay;
    this.exportData.ExportItems[expInd].Beta = newItemData.Beta;
    this.exportData.ExportItems[expInd].StableBalance = newItemData.StableBalance;
    this.exportData.ExportItems[expInd].VintageRunOffResponse = newItemData.VintageRunOffResponse;
    this.exportData.ExportItems[expInd].IsFullyLoaded = checkExportItemHasAllCalibrationsLoaded(newItemData);
  }

  private async processExportData(exportData: IExportData): Promise<IExportData> {
    const result = {
      Acquisitions: this.acquisitions,
      ActiveProductGroups: this.activeProductGroups,
      LastObservationDate: this.lastObservationDate,
      // Response parsing
      ExportItems: exportData.ExportItems.map(exportItem => ({ ...this.parseExportItem(exportItem) })),
      MarketRates: this.marketRateService.parseMarketRatesOnFetch(exportData.MarketRates || []),
      ConcentrationReport: [],
      Settings: exportData.Settings || [],
      CurrentPeriodSummary: []
    };

    if (exportData.ConcentrationReport) {
      if (typeof exportData.ConcentrationReport === 'string') {
        result.ConcentrationReport = JSON.parse(exportData.ConcentrationReport);
      } else {
        result.ConcentrationReport = exportData.ConcentrationReport;
      }
    }

    if (exportData.CurrentPeriodSummary) {
      if (typeof exportData.CurrentPeriodSummary === 'string') {
        result.CurrentPeriodSummary = JSON.parse(exportData.CurrentPeriodSummary);
      } else {
        result.CurrentPeriodSummary = exportData.CurrentPeriodSummary;
      }
    }
    this.exportData = result as IExportData;
    return result as IExportData;
  }

  private parseExportItem(exportItem: IExportItem): IExportItem {
    if (exportItem) {
      const byMostRecentFirst = (a, b) => new Date(b.DateTimeCreated).getTime() - new Date(a.DateTimeCreated).getTime();

      // Properties: Simple parsing
      // exportItem.HasProposeRecords = exportItem.HasProposeRecords;
      // exportItem.HasActiveRecords = exportItem.HasActiveRecords;
      exportItem.IsChecked = exportItem.IsChecked || false; // Not checked on by default // Flag item as It has been fully loaded
      exportItem.IsFullyLoaded = checkExportItemHasAllCalibrationsLoaded(exportItem);

      // VintageRunOffs
      if (exportItem.VintageRunOffResponse) {
        exportItem.VintageRunOffResponse = this.productDetailService.parseVintageRunOffResponse(exportItem.VintageRunOffResponse);
      }

      // StableBalance
      if (exportItem.StableBalance && exportItem.StableBalance.ProductCalibrations.length > 0) {
        exportItem.StableBalance.ProductCalibrations = this.filterChangeLogsFromLatestInactivatedRecord(
          exportItem.StableBalance.ProductCalibrations
        )
          .sort(byMostRecentFirst)
          .map(calibration => {
            calibration.ChangeLogs = this.changelogService.processChangeLogs(calibration.ChangeLogs);
            calibration.ChangeLogs = this.changelogService.parseStableBalanceChangeLogs(calibration.ChangeLogs);

            return calibration;
          });
      }

      // Decay
      if (exportItem.Decay && exportItem.Decay.ProductCalibrations.length > 0) {
        exportItem.Decay.ProductCalibrations = this.filterChangeLogsFromLatestInactivatedRecord(exportItem.Decay.ProductCalibrations)
          .sort(byMostRecentFirst)
          .map(calibration => {
            calibration.ChangeLogs = this.changelogService.processChangeLogs(calibration.ChangeLogs);
            calibration.ChangeLogs = this.changelogService.parseDecayChangeLogs(calibration.ChangeLogs);
            calibration.ExcludeAcquisitionIds = calibration.ExcludeAcquisitionIds || [];
            return calibration;
          });
      }

      // Beta
      if (exportItem.Beta && exportItem.Beta.ProductCalibrations.length > 0) {
        exportItem.Beta.ProductCalibrations = this.filterChangeLogsFromLatestInactivatedRecord(exportItem.Beta.ProductCalibrations)
          .sort(byMostRecentFirst)
          .map(calibration => {
            calibration.ChangeLogs = this.changelogService.processChangeLogs(calibration.ChangeLogs);
            calibration.ChangeLogs = this.changelogService.parseBetaChangeLogs(calibration.ChangeLogs);
            return calibration;
          });
      }
    }

    return exportItem;
  }

  private filterChangeLogsFromLatestInactivatedRecord(calibrations: any[]): any[] {
    const result = [];
    const sortedCalibrations = [];
    const calibrationSort = (a, b) => new Date(b.DateTimeCreated).getTime() - new Date(a.DateTimeCreated).getTime();

    if (this.exportFilterType === EXPORT_TYPES.active.id) {
      let activeFound = false;
      calibrations.sort(calibrationSort).forEach(cal => {
        if (cal.RecordStatus === CalibrationStatus.Active) {
          activeFound = true;
        }
        if (activeFound) {
          sortedCalibrations.push(cal);
        }
      });
      let isInactive = false;
      sortedCalibrations.forEach(calibration => {
        if (!isInactive) {
          result.push(calibration);
        }

        if (calibration.RecordStatus === CalibrationStatus.Inactive) {
          isInactive = true;
        }
      });
    } else {
      let isInactive = false;
      calibrations.sort(calibrationSort).forEach(calibration => {
        if (!isInactive) {
          result.push(calibration);
        }

        if (calibration.RecordStatus === CalibrationStatus.Inactive) {
          isInactive = true;
        }
      });
    }

    return result;
  }

  public initWorkbookExport() {
    this.exportWorkbook$.next(this.exportWorkbook);
  }

  public async rebuildAndExportSingleProduct(productId: number) {
    this.resetValues();
    await this.multiProductExport([productId], false);
  }

  public async multiProductExport(productIds: number[], enableFixedTabsExport: boolean, useActive = false) {
    if (useActive) {
      this.exportFilterType = EXPORT_TYPES.active.id;
    }

    // Build the Spread object we want to export
    const exportWorkbook = await this.buildExportWorkbook(productIds, enableFixedTabsExport);
    // Export It to excel
    if (exportWorkbook) {
      this.exportWorkbook$.next(exportWorkbook);
    }
  }

  private async loadComplementaryProductData(productIds: number[] = []) {
    if (productIds.length > 0) {
      let productsThatRequireMoreDataLoaded: number[] = [];

      if (!this.exportData || !this.exportData.ExportItems || this.exportData.ExportItems.length === 0) {
        productsThatRequireMoreDataLoaded = productIds;
      } else {
        productsThatRequireMoreDataLoaded = this.exportData.ExportItems.filter(
          item =>
            // Is the product in the request ?
            productIds.includes(item.Product.ProductId) &&
            // Has the product his complementary data ?
            !item.IsFullyLoaded
        ).map(({ Product }) => Product.ProductId);
      }

      if (productsThatRequireMoreDataLoaded.length > 0) {
        // Ensure that the essential data is loaded first
        await this.loadExportEssentialData(productsThatRequireMoreDataLoaded);
        await this.buildExportData(productsThatRequireMoreDataLoaded, [], true);
      }
    }
  }

  private async loadComplementaryFixedTabsData() {
    const fixedTabIds = this.fixedTabs.filter(tab => tab.IsChecked).map(({ id }) => id);

    if (fixedTabIds.length > 0) {
      let tabsThatRequireMoreDataLoaded: string[] = [];

      const requiresMoreData =
        !this.exportData || !this.exportData.ExportItems.some(item => !checkExportItemHasAllCalibrationsLoaded(item));
      if (requiresMoreData) {
        tabsThatRequireMoreDataLoaded = this.fixedTabs.map(tab => tab.id);
      } else {
        tabsThatRequireMoreDataLoaded = this.fixedTabs
          .filter(
            ({ id, IsChecked, IsFullyLoaded }) =>
              // Is the tab in the request ?
              IsChecked &&
              fixedTabIds.includes(id) &&
              // Has the tab his complementary data required?
              !IsFullyLoaded
          )
          .map(({ id }) => id);
      }

      if (tabsThatRequireMoreDataLoaded.length > 0) {
        await this.buildExportData([], tabsThatRequireMoreDataLoaded, true);
      }
    }
  }

  private async beforeBuildExportWorkbook(productIds: number[], enableFixedTabsExport: boolean) {
    // If is still retrieving data, wait...
    if (this.retrievingDataIds.length > 0) {
      return new Promise((resolve, reject) =>
        setTimeout(() => resolve(this.beforeBuildExportWorkbook(productIds, enableFixedTabsExport)), 1000)
      );
    }

    // PREPARE THE DATA FOR EXPORT
    if (enableFixedTabsExport) {
      await this.loadComplementaryFixedTabsData();
    } else {
      await this.loadComplementaryProductData(productIds);
    }
    this.filterExportProductCalibrations(); // Filters the calibrations based on the export filter
  }

  public async buildExportWorkbook(productIds: number[], enableFixedTabsExport: boolean): Promise<GC.Spread.Sheets.Workbook> {
    try {
      await this.beforeBuildExportWorkbook(productIds, enableFixedTabsExport);

      // BUILD THE WORKBOOK

      const exportWorkbook = new GC.Spread.Sheets.Workbook();
      // Remove the default created tab
      removeSheetByName(exportWorkbook, 'Sheet1');

      exportWorkbook.options.scrollbarMaxAlign = true;

      // FIXED TAB sheets
      if (enableFixedTabsExport) {
        await this.buildFixedTabsExport(exportWorkbook, this.filteredExportItems);
      }

      // PRODUCT sheets
      await this.buildExportTabsByProduct(
        exportWorkbook,
        this.filteredExportItems.filter(({ Product }) => productIds.includes(Product.ProductId))
      );

      // Select Default Opened Tab
      exportWorkbook.setActiveSheetIndex(0);

      // Store It
      this.exportWorkbook = exportWorkbook;

      return exportWorkbook;
    } catch (err) {
      throw err;
    }
  }

  private async buildExportTabsByProduct(
    exportWorkbook: GC.Spread.Sheets.Workbook,
    filteredExportItems: IExportItem[]
  ): Promise<GC.Spread.Sheets.Workbook> {
    if (filteredExportItems) {
      const charts: IExportChartsOptions[] = [];
      const res = await forkJoin(filteredExportItems.map(i => this.createChartsForExport(i))).toPromise();
      charts.push(...flat((res || []).filter(r => r.length > 0)));

      try {
        const chartsBase64 = await this.getHighChartsImage(charts);
        for (const filteredExportItem of filteredExportItems) {
          await this.buildExportForProduct(exportWorkbook, filteredExportItem, chartsBase64);
        }
      } catch (err) {
        console.error(err);
        this.toastService.showToast(EXPORT_TOASTS.creatingReportError);
      }
    } else {
      console.error(`filteredExportItems is missing in buildExportTabsForProduct`, {
        exportWorkbook,
        filteredExportItems
      });
    }

    return exportWorkbook;
  }

  private async createChartsForExport(exportItem: IExportItem): Promise<IExportChartsOptions[]> {
    const chartOptions = [];
    const exportChartOptions: IExportChartsOptions = getExportChartOptions(exportItem);
    const chartPromises: Promise<any>[] = [];

    // Decay
    if (exportItem.Decay && exportItem.Decay.ProductCalibrations.length > 0) {
      chartPromises.push(
        this.getDecayChartJSON(exportItem, exportChartOptions).then(options => {
          chartOptions.push({
            id: `${exportChartOptions.decay.id}${exportItem.Product.Mnemonic}`,
            options
          });
        })
      );
    }

    // Beta
    if (exportItem.Beta && exportItem.Beta.ProductCalibrations.length > 0) {
      chartPromises.push(
        this.getBetaCharts(exportItem, exportChartOptions).then(betaCharts => {
          chartOptions.push(...betaCharts);
        })
      );
    }

    // Stable Balance
    if (exportItem.StableBalance && exportItem.StableBalance.ProductCalibrations.length > 0) {
      chartPromises.push(
        this.getStableBalanceCharts(exportItem, exportChartOptions).then(options => {
          chartOptions.push({
            id: `${exportChartOptions.stableBalance.id}${exportItem.Product.Mnemonic}`,
            options
          });
        })
      );
    }

    await forkJoin(chartPromises).toPromise();

    return chartOptions;
  }

  private async getDecayChartJSON(exportItem: IExportItem, exportChartOptions: IExportChartsOptions) {
    try {
      const latestCalibration = exportItem.Decay.ProductCalibrations[0];
      if (latestCalibration.DecayCalibrationType === DecayCalibrationType.AvgClosedAccounts) {
        const query: IDecayQuery = {
          ProductId: latestCalibration.ProductId,
          ExcludeAcquisitionIds: latestCalibration.ExcludeAcquisitionIds,
          DecayCalibrationType: latestCalibration.DecayCalibrationType,
          StartDate: latestCalibration.StartDate
        };

        const closedAccounts = await this.decayClosedAccounts.getDecayClosedAccounts(query).toPromise();

        return decayChartJSON(
          exportItem.Decay,
          latestCalibration.DecayEstimateSetting,
          exportChartOptions.decay,
          latestCalibration,
          closedAccounts
        );
      }

      return decayChartJSON(exportItem.Decay, latestCalibration.DecayEstimateSetting, exportChartOptions.decay, latestCalibration);
    } catch (err) {
      console.error(err);
    }
  }

  private async getBetaCharts(exportItem: IExportItem, exportChartOptions: IExportChartsOptions): Promise<IExportChartRequestItem[]> {
    return new Promise<IExportChartRequestItem[]>(async (resolve, reject) => {
      const betaCharts: IExportChartRequestItem[] = [];
      try {
        const betaTimeSeries = exportItem.Beta.ProductTimeSeries;
        const marketRates = await this.marketRateService.getMarketRates().toPromise();

        const estimateResults: IBetaEstimateResult[] =
          exportItem.Beta.ProductCalibrations[0].Regressions && JSON.parse(exportItem.Beta.ProductCalibrations[0].Regressions);

        for (let i = 0; i < estimateResults.length; i++) {
          const est = estimateResults[i];
          const marketRate = marketRates.find(mr => mr.MarketRateId === est.MarketRateId);

          const startDate = new Date(est.PeriodStartDate);
          const endDate = new Date(est.PeriodEndDate);

          const zoomLevel: IZoomLevel = { max: endDate.getTime(), min: startDate.getTime() };

          const betaChartNormal = betaChartJSON(startDate, endDate, clone(betaTimeSeries), clone(marketRate), est, exportChartOptions.beta);
          const betaChartScatter = createScatterPlotJSON(clone(marketRate), clone(betaTimeSeries), est, exportChartOptions.beta, zoomLevel);

          betaCharts.push({
            id: `${exportChartOptions.betaScatterPlots.id}-${i + 1}-${exportItem.Product.Mnemonic}`,
            options: betaChartScatter
          });

          betaCharts.push({
            id: `${exportChartOptions.beta.id}-${i + 1}-${exportItem.Product.Mnemonic}`,
            options: betaChartNormal
          });
        }
        resolve(betaCharts);
      } catch (err) {
        console.warn(`Error Generating Beta Charts`, { exportItem });
        reject(err);
      }
    });
  }

  private async getStableBalanceCharts(exportItem: IExportItem, exportChartOptions: IExportChartsOptions) {
    let chartJSON = {};
    try {
      const stableBalanceCalibration = exportItem.StableBalance.ProductCalibrations[0];
      chartJSON = stableBalanceChartJSON(stableBalanceCalibration, exportChartOptions.stableBalance);
    } catch (error) {
      console.error(error);
    }
    return chartJSON;
  }

  private async buildExportForProduct(workbook: GC.Spread.Sheets.Workbook, exportItem: IExportItem, charts: IExportChartResponseItem[]) {
    let rowIndex = 0;

    const sheetName = prepareSheetNameForExport(`${exportItem.Product.Mnemonic}`);
    const sheet = new GC.Spread.Sheets.Worksheet(sheetName);
    sheet.options.protectionOptions = {
      ...sheet.options.protectionOptions,

      allowSelectLockedCells: true,
      allowSelectUnlockedCells: true,
      allowSort: false,
      allowFilter: false,
      allowEditObjects: false,
      allowResizeRows: false,
      allowDragInsertRows: false,
      allowDragInsertColumns: false,
      allowInsertRows: false,
      allowInsertColumns: false,
      allowDeleteRows: false,
      allowDeleteColumns: false
    };
    sheet.options.isProtected = true;
    // Setting Zoom #8202
    sheet.zoom(0.6);

    sheet.setRowCount(this.MAX_ROW_COUNT);
    sheet.setColumnCount(this.MAX_COL_COUNT);

    // // Setting a high initial row count so all items can be added
    // sheet.setRowCount(2000);

    // Cell Size (width & height)
    sheet.defaults.colWidth = CELL_WIDTH;
    sheet.defaults.rowHeight = CELL_HEIGHT;

    const startRow = 0;
    // STABLE BALANCE Export
    rowIndex = await this.buildStableBalanceExport(
      sheet,
      startRow,
      getStartColumn(EXPORT_CONTENT_TYPES.StableBalance, exportItem),
      exportItem,
      charts
    );

    // DECAY Export
    rowIndex = await this.buildDecayExport(sheet, startRow, getStartColumn(EXPORT_CONTENT_TYPES.Decay, exportItem), exportItem, charts);

    // BETA Export
    rowIndex = await this.buildBetaExport(sheet, startRow, getStartColumn(EXPORT_CONTENT_TYPES.Beta, exportItem), exportItem, charts);

    // Product Detail Export
    rowIndex = await this.buildProductDetailExport(
      sheet,
      startRow,
      getStartColumn(EXPORT_CONTENT_TYPES.ProductDetail, exportItem),
      exportItem
    );

    setRowsAndColumns(sheet);

    sheet.resumeEvent();
    addSheet(workbook, sheet);

    return workbook;
  }

  // private setRowsAndColumns(sheet: GC.Spread.Sheets.Worksheet) {
  //   // Trim sheet rows to have just 3 bottom empty ones, and columns to have just 1 right empty one.
  //   const sheetRows = sheet.getArray(0, 0, sheet.getRowCount(), sheet.getColumnCount());
  //   const bools = sheetRows.map(r => r.some(Boolean));
  //   const lastNonEmptyIndex = bools.reverse().findIndex(r => r);
  //   // const rowCount = Math.max(100, bools.length - lastNonEmptyIndex + 3);
  //   const rowCount = bools.length - lastNonEmptyIndex + 3;
  //   sheet.setRowCount(rowCount);

  //   console.log({
  //     sheetRows,
  //     bools,
  //     lastNonEmptyIndex,
  //     rowCount
  //   });
  // }

  // ============
  //  DECAY
  // ============
  private async buildDecayExport(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    charts: IExportChartResponseItem[]
  ): Promise<number> {
    try {
      const calibrations = exportItem.Decay.ProductCalibrations;

      if (calibrations && calibrations.length > 0) {
        const functionalForms = this.functionalFormService.getFunctionalFormsForDecay();

        // Title
        rowIndex++;
        this.createCalibrationTitle(sheet, `Decay - ${exportItem.Product.ProductName}`, rowIndex, startCol);

        // Decay Chart
        rowIndex += 2;
        const chartStartRow = rowIndex;
        rowIndex = await this.addDecayChartToSheet(sheet, rowIndex, startCol, exportItem, calibrations, charts);
        rowIndex += 2;

        // Estimate Detail Sheet
        let currentCol = 0;

        if (calibrations[0].DecayCalibrationType !== DecayCalibrationType.AvgClosedAccounts) {
          [rowIndex, currentCol] = addDecayEstimateDetailToSheet(
            sheet,
            rowIndex,
            startCol,
            exportItem.Product,
            calibrations,
            this.exportData.Acquisitions,
            functionalForms
          );
        } else {
          currentCol = startCol + 12;
          // 12 => Amount of headers and spans on @components/export/spread/decay/decay-estimate-detail.populateHeaders()
        }

        // Decay Factors
        currentCol += 3;
        addDecayFactorsToSheet(sheet, chartStartRow, currentCol, exportItem, this.exportFilterType === EXPORT_TYPES.active.id);

        // Add changelog
        const changeLogs = [];
        calibrations.forEach(calibration => {
          calibration.ChangeLogs.forEach(changeLog => {
            changeLogs.push(changeLog);
          });
        });

        if (calibrations[calibrations.length - 1].ChangeLogs.length === 0) {
          const calibrationChange: ICalibrationChangelog<IDecayCalibration> = {
            Calibration: calibrations[calibrations.length - 1],
            CalibrationId: calibrations[calibrations.length - 1].CalibrationId,
            CalibrationJSON: JSON.stringify(calibrations[calibrations.length - 1]),
            CalibrationType: CALIBRATION_TYPES.Decay.toString(),
            DateTime: new Date().toJSON(),
            ChangelogId: -1,
            ProductId: calibrations[calibrations.length - 1].ProductId,
            Comment: null,
            UserId: null,
            Summary: []
          };

          changeLogs.push(calibrationChange);
          this.changelogService.parseDecayChangeLogs(changeLogs);
          changeLogs.pop();
        } else {
          this.changelogService.parseDecayChangeLogs(changeLogs);
        }

        calibrations.forEach(calibration => {
          const filteredChangeLogs = changeLogs.filter(({ CalibrationId }) => CalibrationId === calibration.DecayCalibrationId);
          calibration.ChangeLogs = filteredChangeLogs;
        });

        this.changelogService.parseDecayChangeLogs(changeLogs);

        calibrations.forEach(calibration => {
          const filteredChangeLogs = changeLogs.filter(({ CalibrationId }) => CalibrationId === calibration.DecayCalibrationId);
          calibration.ChangeLogs = filteredChangeLogs;
        });

        const calibrationTypes = await this.decayCalibrationTypeService.getDecayCalibrationTypes().toPromise();
        // Change Logs
        rowIndex += 2;
        rowIndex = addDecayChangeLogToSheet(
          sheet,
          rowIndex,
          startCol,
          exportItem.Product,
          calibrations,
          functionalForms,
          this.exportData.Acquisitions,
          calibrationTypes,
          this.changelogService.getDecayChangelogDiff
        );
      }
    } catch (err) {
      console.error('Error building decay export', err);
    }

    return rowIndex;
  }

  private async addDecayChartToSheet(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    calibrations: IDecayCalibration[],
    charts: IExportChartResponseItem[]
  ): Promise<number> {
    try {
      // Add chart
      if (calibrations && calibrations.length > 0) {
        const exportChartOptions = getExportChartOptions(exportItem);
        const chartId = `${exportChartOptions.decay.id}${exportItem.Product.Mnemonic}`;
        let chartBase64 = charts.find(item => item.id === chartId);
        if (!chartBase64) {
          console.warn('Decay chart not found re-fetching');
          const chartJSON = await this.getDecayChartJSON(exportItem, exportChartOptions);
          chartBase64 = await this.getHighChartsImage([{ id: chartId, options: chartJSON }])[0];
        }

        sheet
          .getCell(rowIndex, startCol)
          .sheet.pictures.add(
            chartId,
            chartBase64.image,
            exportChartOptions.decay.posX,
            rowIndex * CELL_HEIGHT,
            exportChartOptions.decay.width,
            exportChartOptions.decay.height
          );
        rowIndex += exportChartOptions.decay.rowSpaceUsed + 1;
      }
    } catch (err) {
      console.error('Unable to export the Decay chart from', exportItem, err);
    }
    return rowIndex;
  }

  // ============
  //  BETA
  // ============

  private async buildBetaExport(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    charts: IExportChartResponseItem[]
  ): Promise<number> {
    try {
      // Get the required data
      const calibrations = exportItem.Beta.ProductCalibrations;
      const product = exportItem.Product;
      const marketRates = await this.marketRateService.getMarketRates().toPromise();

      let startDate;
      let endDate;

      // Get data required
      if (calibrations && calibrations.length > 0 && calibrations[0]) {
        const estimateResults: IBetaEstimateResult[] = calibrations[0].Regressions && JSON.parse(calibrations[0].Regressions);

        if (estimateResults) {
          startDate =
            estimateResults.length > 0 &&
            estimateResults[0] &&
            estimateResults[0].PeriodStartDate &&
            new Date(estimateResults[0].PeriodStartDate);
          endDate =
            estimateResults.length > 0 &&
            estimateResults[0] &&
            estimateResults[0].PeriodEndDate &&
            new Date(estimateResults[0].PeriodEndDate);

          // Populate data
          rowIndex++;
          this.createCalibrationTitle(sheet, `Beta - ${exportItem.Product.ProductName}`, rowIndex, startCol);
          rowIndex += 2;

          const productTimeSeries = exportItem.Beta.ProductTimeSeries;
          if (startDate && endDate && productTimeSeries) {
            // tslint:disable-next-line: prefer-for-of
            for (let i = 0; i < estimateResults.length; i++) {
              const est = estimateResults[i];
              // FOR EACH ESTIMATE:
              // ------------------

              // -- Estimate Name
              const titleCell = sheet.getCell(rowIndex, startCol);
              titleCell.text(`${est.Name}`);

              rowIndex++;

              // -- Estimate Chart
              const chartsStartRow = rowIndex;
              rowIndex = await this.addBetaEstimateChartToSheet(sheet, chartsStartRow, startCol, exportItem, i, charts);

              // -- ScatterPlot Chart
              rowIndex = await this.addBetaEstimateScatterPlotToSheet(sheet, chartsStartRow, startCol, exportItem, i, charts);
              rowIndex += 2;
            }

            rowIndex++;
          }
          rowIndex = addBetaEstimatesToSheet(sheet, rowIndex, startCol, estimateResults, marketRates);

          // Add changelog
          const changeLogs = [];
          calibrations.forEach(calibration => {
            calibration.ChangeLogs.forEach(changeLog => {
              changeLogs.push(changeLog);
            });
          });

          if (calibrations[calibrations.length - 1].ChangeLogs.length === 0) {
            const calibrationChange: ICalibrationChangelog<IBetaCalibration> = {
              Calibration: calibrations[calibrations.length - 1],
              CalibrationId: calibrations[calibrations.length - 1].CalibrationId,
              CalibrationJSON: JSON.stringify(calibrations[calibrations.length - 1]),
              CalibrationType: CALIBRATION_TYPES.Beta.toString(),
              DateTime: new Date().toJSON(),
              ChangelogId: -1,
              ProductId: calibrations[calibrations.length - 1].ProductId,
              Comment: null,
              UserId: null,
              Summary: []
            };

            changeLogs.push(calibrationChange);
            this.changelogService.parseBetaChangeLogs(changeLogs);
            changeLogs.pop();
          } else {
            this.changelogService.parseBetaChangeLogs(changeLogs);
          }

          calibrations.forEach(calibration => {
            const filteredChangeLogs = changeLogs.filter(({ CalibrationId }) => CalibrationId === calibration.BetaCalibrationId);
            calibration.ChangeLogs = filteredChangeLogs;
          });

          this.changelogService.parseBetaChangeLogs(changeLogs);

          calibrations.forEach(calibration => {
            const filteredChangeLogs = changeLogs.filter(({ CalibrationId }) => CalibrationId === calibration.BetaCalibrationId);
            calibration.ChangeLogs = filteredChangeLogs;
          });

          rowIndex += 2;
          rowIndex = addBetaChangeLogToSheet(
            sheet,
            rowIndex,
            startCol,
            exportItem.Product,
            calibrations,
            marketRates,
            this.changelogService.getBetaChangeLogDiff
          );
        }
      }
    } catch (err) {
      console.error('Error building beta export', err);
    }

    return rowIndex;
  }

  private async addBetaEstimateChartToSheet(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    index: number,
    charts: IExportChartResponseItem[]
  ): Promise<number> {
    try {
      if (exportItem.Beta.ProductCalibrations.length > 0) {
        // Add chart
        const exportChartOptions = getExportChartOptions(exportItem);
        const chartId = `${exportChartOptions.beta.id}-${index + 1}-${exportItem.Product.Mnemonic}`;
        const chartBase64 = charts.find(({ id }) => id === chartId);
        if (chartBase64) {
          sheet
            .getCell(rowIndex, startCol)
            .sheet.pictures.add(
              chartId,
              chartBase64.image,
              exportChartOptions.beta.posX,
              rowIndex * CELL_HEIGHT,
              exportChartOptions.beta.width,
              exportChartOptions.beta.height
            );
          rowIndex += exportChartOptions.beta.rowSpaceUsed + 2;
        } else {
          // Lets attempt to re-fetch the chart
          console.warn(`Re-fetching Beta Chart`, { exportItem });
          const chartJSON: IExportChartRequestItem[] = await this.getBetaCharts(exportItem, exportChartOptions);
          const chartBase64Fetched = await this.getHighChartsImage(chartJSON);
          sheet
            .getCell(rowIndex, startCol)
            .sheet.pictures.add(
              chartId,
              chartBase64Fetched[1].image,
              exportChartOptions.beta.posX,
              rowIndex * CELL_HEIGHT,
              exportChartOptions.beta.width,
              exportChartOptions.beta.height
            );
          rowIndex += exportChartOptions.beta.rowSpaceUsed + 2;
        }
      }
    } catch (err) {
      console.error('Unable to export the Beta chart from', exportItem, err);
    }
    return rowIndex;
  }

  /**
   * This method will add multiple charts to a workbook
   */
  private async addBetaEstimateScatterPlotToSheet(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    index: number,
    charts: IExportChartResponseItem[]
  ) {
    try {
      const exportChartOptions = getExportChartOptions(exportItem);
      const chartId = `${exportChartOptions.betaScatterPlots.id}-${index + 1}-${exportItem.Product.Mnemonic}`;
      let chartBase64 = charts.find(({ id }) => id === chartId);
      if (!chartBase64) {
        console.warn('Re-Fetching Beta scatter plot ');
        const chartJSON: IExportChartRequestItem[] = await this.getBetaCharts(exportItem, exportChartOptions);
        chartBase64 = await this.getHighChartsImage(chartJSON)[0];
      }

      sheet.getCell(rowIndex, startCol).sheet.pictures.add(
        chartId,
        chartBase64.image,
        exportChartOptions.betaScatterPlots.posX,
        // Accumulative/Relative vertical position of the origin of each scatter plot chart
        rowIndex * CELL_HEIGHT,
        exportChartOptions.betaScatterPlots.width,
        exportChartOptions.betaScatterPlots.height
      );
      rowIndex += exportChartOptions.betaScatterPlots.rowSpaceUsed + 1;
    } catch (err) {
      console.error('Unable To Export Beta Scatter Plot from', exportItem, err);
    }

    return rowIndex;
  }

  // ============
  //  STABLE BALANCE
  // ============

  private async buildStableBalanceExport(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    charts: IExportChartResponseItem[]
  ): Promise<number> {
    try {
      // Get the required data
      const calibrations = exportItem.StableBalance.ProductCalibrations;

      if (calibrations && calibrations.length > 0) {
        const latestCalibration: IStableBalanceCalibration = calibrations[0];

        // // Get data required
        // const functionalForms = this.functionalFormService.getFunctionalForms();

        // Add calibration to sheet
        rowIndex++;

        this.createCalibrationTitle(sheet, `Stable Balance - ${exportItem.Product.ProductName}`, rowIndex, startCol);

        rowIndex += 2;
        // Create the sheets in the workbook
        // -- Chart
        rowIndex = await this.addStableBalanceChartToSheet(sheet, rowIndex, startCol, exportItem, charts);
        // -- Model Fit
        rowIndex += 2;
        const acquisitionCoefficients = exportItem.StableBalance.AcquisitionCoefficients;
        rowIndex = addStableBalanceModelFitToSheet(
          sheet,
          rowIndex,
          startCol,
          latestCalibration,
          this.exportData.Acquisitions,
          acquisitionCoefficients
        );

        // -- Historical Haircut
        this.addHistoricalHaircutInfoToSheet(sheet, exportItem.StableBalance.ProductCalibrations, rowIndex + 2, startCol + 16);

        // -- Change Log
        rowIndex += 2;
        rowIndex = await this.addStableBalanceChangelogToSheet(
          sheet,
          rowIndex,
          startCol,
          exportItem,
          calibrations,
          this.exportData.Acquisitions
        );
      }
    } catch (err) {
      console.error('Error building stable balance export', err);
    }
    return rowIndex;
  }

  private async addStableBalanceChartToSheet(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    charts: IExportChartResponseItem[]
  ): Promise<number> {
    try {
      if (charts) {
        // Add chart
        const exportChartOptions = getExportChartOptions(exportItem);
        const chartId = `${exportChartOptions.stableBalance.id}${exportItem.Product.Mnemonic}`;
        let chartBase64 = charts.find(item => item.id === chartId);
        if (!chartBase64) {
          console.warn('Missing Stable Balance Chart Re-fetching');
          const chartJSON = await this.getStableBalanceCharts(exportItem, exportChartOptions);
          chartBase64 = await this.getHighChartsImage([{ id: chartId, options: chartJSON }])[0];
        }

        sheet
          .getCell(rowIndex, startCol)
          .sheet.pictures.add(
            chartId,
            chartBase64.image,
            exportChartOptions.stableBalance.posX,
            rowIndex * CELL_HEIGHT,
            exportChartOptions.stableBalance.width,
            exportChartOptions.stableBalance.height
          );
        rowIndex += exportChartOptions.stableBalance.rowSpaceUsed + 1;
      } else {
        console.warn(`There is no Stable Balance Chart Data`, { exportItem });
      }
    } catch (err) {
      console.error('Unable to export the Stable Balance chart from', exportItem, err);
    }

    return rowIndex;
  }

  private async addStableBalanceChangelogToSheet(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    exportItem: IExportItem,
    calibrations: IStableBalanceCalibration[],
    acquisitions: IAcquisition[]
  ) {
    try {
      // Add changelog
      if (calibrations && calibrations.length > 0) {
        // Add changelog
        const changeLogs = [];
        calibrations.forEach(calibration => {
          calibration.ChangeLogs.forEach(changeLog => {
            changeLogs.push(changeLog);
          });
        });

        if (calibrations[calibrations.length - 1].ChangeLogs.length === 0) {
          const calibrationChange: ICalibrationChangelog<IStableBalanceCalibration> = {
            Calibration: calibrations[calibrations.length - 1],
            CalibrationId: calibrations[calibrations.length - 1].CalibrationId,
            CalibrationJSON: JSON.stringify(calibrations[calibrations.length - 1]),
            CalibrationType: CALIBRATION_TYPES.StableBalance.toString(),
            DateTime: new Date().toJSON(),
            ChangelogId: -1,
            ProductId: calibrations[calibrations.length - 1].ProductId,
            Comment: null,
            UserId: null,
            Summary: []
          };

          changeLogs.push(calibrationChange);
          this.changelogService.parseStableBalanceChangeLogs(changeLogs);
          changeLogs.pop();
        } else {
          this.changelogService.parseStableBalanceChangeLogs(changeLogs);
        }

        calibrations.forEach(calibration => {
          const filteredChangeLogs = changeLogs.filter(({ CalibrationId }) => CalibrationId === calibration.CalibrationId);
          calibration.ChangeLogs = filteredChangeLogs;
        });

        this.changelogService.parseStableBalanceChangeLogs(changeLogs);

        calibrations.forEach(calibration => {
          const filteredChangeLogs = changeLogs.filter(({ CalibrationId }) => CalibrationId === calibration.CalibrationId);
          calibration.ChangeLogs = filteredChangeLogs;
        });

        rowIndex = addStableBalanceChangeLogToSheet(
          sheet,
          rowIndex,
          startCol,
          calibrations,
          acquisitions,
          this.changelogService.getStableBalanceChangelogDiff
        );
      }
    } catch (err) {
      console.error('Unable to export the Stable Balance changelog from', exportItem, err);
    }

    return rowIndex;
  }

  public addHistoricalHaircutInfoToSheet = (
    sheet: GC.Spread.Sheets.Worksheet,
    calibrations: IStableBalanceCalibration[],
    rowIndex: number,
    colOffSet: number
  ): number => {
    const forecastEndDateIndex = calibrations[0].NonStableBalances.findIndex(
      bal => new Date(bal.PeriodEndDate).toJSON().substr(0, 7) === new Date(calibrations[0].ForecastStartDate).toJSON().substr(0, 7)
    );

    const nonStablePercentBalances = ((calibrations && calibrations[0].NonStablePercents) || [])
      .splice(0, forecastEndDateIndex)
      .map(({ Balance }) => Balance);

    if (nonStablePercentBalances && nonStablePercentBalances.length > 0) {
      rowIndex = createHaircutInfo(sheet, nonStablePercentBalances, calibrations[0], rowIndex, colOffSet);
    } else {
      console.error('Error: There is no nonStablePercentBalances to build the Historical Haircut of', calibrations[0]);
    }

    return rowIndex;
  };

  //  ===============
  //  Product Detail
  //  ===============

  private async buildProductDetailExport(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number, exportItem: IExportItem) {
    try {
      let vintageRunOff: IVintageRunOff;
      let vintageRunOffSchedule;

      if (this.exportFilterType === EXPORT_TYPES.propose.id) {
        // Finding vintage run off
        vintageRunOff = exportItem.VintageRunOffResponse.VintageRunOffs.find(
          ({ RunOffType }) => RunOffType === VintageRunOffType.ProposedActual
        );

        if (!vintageRunOff) {
          vintageRunOff = exportItem.VintageRunOffResponse.VintageRunOffs.find(
            ({ RunOffType }) => RunOffType === VintageRunOffType.ActiveActual
          );
        }

        // Finding run off schedule
        vintageRunOffSchedule = exportItem.VintageRunOffResponse.VintageRunOffSchedule.find(
          ({ RunOffType }) => RunOffType === VintageRunOffType.ProposedActual
        );
        if (!vintageRunOffSchedule) {
          vintageRunOffSchedule = exportItem.VintageRunOffResponse.VintageRunOffSchedule.find(
            ({ RunOffType }) => RunOffType === VintageRunOffType.ActiveActual
          );
        }
      } else if (this.exportFilterType === EXPORT_TYPES.active.id) {
        vintageRunOff = exportItem.VintageRunOffResponse.VintageRunOffs.find(
          ({ RunOffType }) => RunOffType === VintageRunOffType.ActiveActual
        );

        vintageRunOffSchedule = exportItem.VintageRunOffResponse.VintageRunOffSchedule.find(
          ({ RunOffType }) => RunOffType === VintageRunOffType.ActiveActual
        );
      }

      const forecastEndDateSetting = this.exportData.Settings.find(x => x.SettingName === 'FORECAST_END_DATE');
      const calibrationEndDateSetting = this.exportData.Settings.find(x => x.SettingName === 'CALIBRATION_END_DATE');

      if (vintageRunOff && vintageRunOffSchedule && forecastEndDateSetting && calibrationEndDateSetting) {
        const forecastStartDate = getLastDayOfNextMonth(new Date(calibrationEndDateSetting.SettingValue));
        const forecastEndDate = new Date(forecastEndDateSetting.SettingValue);
        // 🛠 Fix for 9318 need to remove data after forecast end date
        const forecastEndDateIndex = vintageRunOff.VintageRunOffTimeSeries.findIndex(
          x => x.PeriodEndDate.substr(0, 7) === forecastEndDate.toJSON().substr(0, 7)
        );
        vintageRunOff.VintageRunOffTimeSeries = vintageRunOff.VintageRunOffTimeSeries.slice(0, forecastEndDateIndex + 1);

        // Adding Data To Sheets
        rowIndex++;
        this.createCalibrationTitle(sheet, `Product Detail - ${exportItem.Product.ProductName}`, rowIndex, startCol);
        rowIndex += 2;
        // Adding actual sheet
        // Removing Bad
        rowIndex = await this.addProductDetailActualToSpread(sheet, rowIndex, startCol, vintageRunOff, forecastStartDate, forecastEndDate);
        startCol += 8;
        await this.addRunOffScheduleToSpread(sheet, rowIndex, startCol, vintageRunOffSchedule);
      }
      // else {
      //   if (!exportItem.VintageRunOffResponse.VintageRunOffs) {
      //     console.warn('VintageRunOffResponse.VintageRunOffs is missing in', exportItem);
      //   }
      //   if (!exportItem.VintageRunOffResponse.VintageRunOffSchedule) {
      //     console.warn('VintageRunOffResponse.VintageRunOffSchedule is missing in', exportItem);
      //   }
      // }
    } catch (err) {
      console.error('Error Building Product Detail from', exportItem, err);
    }

    return rowIndex;
  }

  private async addProductDetailActualToSpread(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    vintageRunOff: IVintageRunOff,
    forecastStartDate: Date,
    forecastEndDate: Date
  ) {
    try {
      createProductDetailActualExport(sheet, rowIndex, startCol, vintageRunOff, forecastStartDate, forecastEndDate);
    } catch (error) {
      console.error('Error Adding Product Detail Actual To Spread');
    }
    return rowIndex;
  }

  private async addRunOffScheduleToSpread(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    startCol: number,
    vintageRunOffSchedule: IVintageRunOffSchedule
  ) {
    try {
      createRunOffScheduleExport(sheet, rowIndex, startCol, vintageRunOffSchedule);
    } catch (err) {
      console.error('Error Building Run Off Schedule ', err);
    }
  }

  // ============
  //  FIXED TABS
  // ============
  private async buildFixedTabsExport(
    workbook: GC.Spread.Sheets.Workbook,
    filteredExportItems: IExportItem[]
  ): Promise<GC.Spread.Sheets.Workbook> {
    const checkedTabs = this.fixedTabs.filter(({ IsChecked }) => IsChecked);
    const marketRates: IMarketRate[] = await this.marketRateService.getMarketRates().toPromise();
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < checkedTabs.length; i++) {
      const tab = checkedTabs[i];
      const sheetName = prepareSheetNameForExport(tab.displayName);
      const sheet = new GC.Spread.Sheets.Worksheet(sheetName);
      sheet.options.protectionOptions = {
        ...sheet.options.protectionOptions,

        allowSelectLockedCells: true,
        allowSelectUnlockedCells: true,
        allowSort: false,
        allowFilter: false,
        allowEditObjects: false,
        allowResizeRows: false,
        allowDragInsertRows: false,
        allowDragInsertColumns: false,
        allowInsertRows: false,
        allowInsertColumns: false,
        allowDeleteRows: false,
        allowDeleteColumns: false
      };
      sheet.options.isProtected = true;
      // Tab colour
      sheet.options.sheetTabColor = tab.tabColor;

      // Cell Size (width & height)
      sheet.defaults.colWidth = CELL_WIDTH;
      sheet.defaults.rowHeight = CELL_HEIGHT;

      const calibrationFilter: CalibrationFilter = isActive;

      switch (tab.id) {
        case 'calibration-settings':
          const decayCalibrationTypes = await this.decayCalibrationTypeService.getDecayCalibrationTypes().toPromise();
          createCalibrationSettingsExport(
            sheet,
            filteredExportItems,
            this.exportData.Acquisitions,
            marketRates,
            calibrationFilter,
            this.exportFilterType === EXPORT_TYPES.propose.id ? CalibrationStatus.Proposed : CalibrationStatus.Active,
            decayCalibrationTypes
          );

          break;
        case 'product-summary':
          await this.addProductSummaryToSpread(sheet, filteredExportItems, this.exportData.ActiveProductGroups, this.lastObservationDate);
          break;
        case 'concentration-report':
          await this.addConcentrationReportToSpread(
            sheet,
            filteredExportItems,
            this.exportData.ActiveProductGroups,
            this.lastObservationDate
          );
          break;
      }

      setRowsAndColumns(sheet);
      sheet.resumeEvent();
      addSheet(workbook, sheet);
    }

    return workbook;
  }

  private async addConcentrationReportToSpread(
    sheet: GC.Spread.Sheets.Worksheet,
    filteredExportItems: IExportItem[],
    productGroups: IProductGroup[],
    lastObservationDate: Date
  ) {
    const rowIndex = 1;
    const colIndex = 1;

    try {
      const products = filteredExportItems.map(({ Product }) => Product);

      const concentrations: IProductConcentrationDetails[] = this.exportData.ConcentrationReport.filter(con =>
        products.map(({ ProductId }) => ProductId).includes(con.ProductId)
      );

      createConcentrationReportExport(sheet, rowIndex, colIndex, products, productGroups, concentrations, lastObservationDate);
    } catch (err) {
      console.error('Error Creating Product Concentration Report', err);
    }
  }

  private async addProductSummaryToSpread(
    sheet: GC.Spread.Sheets.Worksheet,
    filteredExportItems: IExportItem[],
    productGroups: IProductGroup[],
    lastObservationDate: Date
  ) {
    try {
      let rowIndex = 1;
      const colIndex = 1;

      // Getting preliminary data
      const products = filteredExportItems.map(({ Product }) => Product);
      const cpSummary: IProductSummaryResponse[] = this.exportData.CurrentPeriodSummary;
      cpSummary.forEach(cp => {
        // Finding relevant export item
        const exportItem = this.exportData.ExportItems.find(val => val.Product.ProductId === cp.ProductID);
        if (exportItem && exportItem.Wal && exportItem.Wal.length > 0) {
          const foundWal = exportItem.Wal.find(({ RunOffType }) => RunOffType === VintageRunOffType.ActiveActual);

          if (foundWal) {
            // @ts-ignore
            cp.AverageLife = round(foundWal.Wal, 2);
          } else {
            // @ts-ignore
            cp.AverageLife = null;
          }
        }
      });

      const [productData, totals] = await buildProductSummaryData(products, productGroups, cpSummary);
      rowIndex = await this.addProductSummaryChart(sheet, rowIndex, productData, totals);
      rowIndex = createProductSummaryExport(sheet, rowIndex, colIndex, lastObservationDate, productData, totals);

      return rowIndex;
    } catch (err) {
      console.error('Error adding product summary to spread', err);
    }
  }

  private async addProductSummaryChart(
    sheet: GC.Spread.Sheets.Worksheet,
    rowIndex: number,
    productData: any[],
    totals: IProductCPSummaryItem
  ): Promise<number> {
    const chartOptions = productSummaryChartOptions;
    const chartJSON = productSummaryChartJSON(productData, totals);
    const chartBase64 = await this.getHighChartsImage(
      [{ options: chartJSON, id: chartOptions.id }],
      chartOptions.width * 3 // Multiplied to get a source image with a better resolution
    );

    // Ensure to add the data before the sheet otherwise it will look stretched
    if (chartBase64) {
      sheet.pictures.add(
        chartBase64[0].id,
        chartBase64[0].image,
        chartOptions.posX,
        chartOptions.posY,
        chartOptions.width,
        chartOptions.height
      );
      // Merge cells behind
      sheet.addSpan(0, 0, 1 + chartOptions.rowSpaceUsed, 1 + chartOptions.colSpaceUsed);

      rowIndex += chartOptions.rowSpaceUsed + 2;
    }

    return rowIndex;
  }

  private createCalibrationTitle(sheet: GC.Spread.Sheets.Worksheet, title: string, rowIndex: number, colIndex: number) {
    sheet.addSpan(rowIndex, colIndex, 2, 6);
    sheet
      .getCell(rowIndex, colIndex)
      .text(title)
      .font(`bold 15pt ${FONT_NAME}`);
    sheet.getCell(rowIndex, colIndex).textDecoration(GC.Spread.Sheets.TextDecorationType.underline);
  }
}
